import React from 'react';
import { GraduationCap } from '@phosphor-icons/react';

interface CompletionRow {
    employeeName: string;
    userId: string;
    totalEnrollments: number;
    completed: number;
    completionRate: number;
}

interface TeamCourseCompletionWidgetProps {
    data?: CompletionRow[] | null;
    loading?: boolean;
    error?: string;
}

const getInitials = (name: string): string =>
    name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);

const getCompletionColor = (rate: number) => {
    if (rate >= 60) return { bar: 'bg-emerald-500', text: 'text-emerald-600', avatar: 'bg-emerald-100 text-emerald-700' };
    if (rate >= 30) return { bar: 'bg-amber-500', text: 'text-amber-600', avatar: 'bg-amber-100 text-amber-700' };
    return { bar: 'bg-red-500', text: 'text-red-600', avatar: 'bg-red-100 text-red-700' };
};

export const TeamCourseCompletionWidget: React.FC<TeamCourseCompletionWidgetProps> = ({ data, loading, error }) => {
    const rows = Array.isArray(data) ? data : [];

    return (
        <div className="flex flex-col h-full">
            <div className="px-4 pt-4 pb-4 flex items-center justify-between">
                <span className="text-xs text-gray-400">{rows.length} reports</span>
            </div>
            <div className="flex-1 overflow-auto px-4 pb-4">
                {loading ? (
                    <div className="flex items-center justify-center h-full">
                        <div className="w-6 h-6 border-3 border-gray-200 border-t-primary rounded-full animate-spin" />
                    </div>
                ) : error ? (
                    <div className="flex items-center justify-center h-full text-red-500 text-sm">{error}</div>
                ) : rows.length === 0 ? (
                    <div className="flex flex-col items-center justify-center h-full text-gray-400 gap-2">
                        <GraduationCap className="w-8 h-8" />
                        <span className="text-sm">No enrollment data</span>
                    </div>
                ) : (
                    <div className="space-y-3">
                        {rows.map((row) => {
                            const rate = Number(row.completionRate) || 0;
                            const total = Number(row.totalEnrollments) || 0;
                            const completed = Number(row.completed) || 0;
                            const colors = getCompletionColor(rate);
                            return (
                                <div key={row.userId} className="flex items-center gap-3">
                                    <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold shrink-0 ${colors.avatar}`}>
                                        {getInitials(row.employeeName || '??')}
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <div className="flex items-center justify-between mb-1">
                                            <span className="text-sm text-gray-800 truncate">{row.employeeName}</span>
                                            <span className={`text-xs font-medium ${colors.text} shrink-0 ml-2`}>
                                                {completed}/{total} ({rate}%)
                                            </span>
                                        </div>
                                        <div className="w-full h-2 bg-gray-100 rounded-full overflow-hidden">
                                            <div
                                                className={`h-full ${colors.bar} rounded-full transition-all duration-500`}
                                                style={{ width: `${rate}%` }}
                                            />
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>
        </div>
    );
};
